import React from "react";
import { ChevronDown, BookOpen, Zap, Layers } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SequenceRow } from "./SequenceRow";
import { cn } from "@/lib/utils";
import type { CourseNodeWithStatus } from "@/types/courses";

interface ModuleSectionProps {
  title: string;
  moduleIndex: number;
  nodes: CourseNodeWithStatus[];
  contentStepCounts: Map<string, number>;
  isModuleOpen: boolean;
  onToggleModule: () => void;
  openRowIds: Set<string>;
  onToggleRow: (id: string) => void;
  onNodeAction: (nodeId: string) => void;
  /** Global sequential index offset for numbering */
  indexOffset: number;
}

export const ModuleSection: React.FC<ModuleSectionProps> = ({
  title,
  moduleIndex,
  nodes,
  contentStepCounts,
  isModuleOpen,
  onToggleModule,
  openRowIds,
  onToggleRow,
  onNodeAction,
  indexOffset
}) => {
  const totalSteps = nodes.reduce((s, n) => s + (contentStepCounts.get(n.id) ?? 0), 0);
  const totalXp = nodes.reduce((s, n) => s + n.xpReward, 0);
  const completedCount = nodes.filter((n) => n.status === "completed").length;
  const completionPct = nodes.length > 0 ? Math.round(completedCount / nodes.length * 100) : 0;

  return (
    <Collapsible open={isModuleOpen} onOpenChange={onToggleModule}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 px-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring py-[26px]">

          <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase shrink-0">
            Module {moduleIndex + 1}
          </span>
          <span className="truncate flex-1 text-base font-medium">{title}</span>

          {/* Compact stats */}
          <div className="hidden sm:flex items-center gap-3 text-sm text-muted-foreground shrink-0">
            <span className="flex items-center gap-1">
              <BookOpen className="h-3 w-3" />
              {nodes.length}
            </span>
            <span className="flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {totalSteps}
            </span>
            <span className="flex items-center gap-1">
              <Zap className="h-3 w-3" />
              {totalXp}
            </span>
          </div>

          {/* Completion */}
          <div className="flex items-center gap-2 shrink-0">
            <Progress value={completionPct} className="h-1 w-12" />
            <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">
              {completionPct}%
            </span>
          </div>

          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform shrink-0",
              isModuleOpen && "rotate-180"
            )} />

        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="divide-y divide-border">
          {nodes.map((node, i) =>
          <SequenceRow
            key={node.id}
            node={node}
            index={indexOffset + i}
            stepCount={contentStepCounts.get(node.id) ?? 0}
            isOpen={openRowIds.has(node.id)}
            onToggle={() => onToggleRow(node.id)}
            onNodeAction={onNodeAction} />

          )}
        </div>
      </CollapsibleContent>
    </Collapsible>);

};